/** 来源：foodbuy/market Button；保留公共 variant/loading API，并吸收 market 的 loadingLabel。 */
import { forwardRef, useContext, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { FormSubmitContext } from './form-context'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'ghostDanger' | 'icon' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const variantClasses: Record<ButtonVariant, string> = {
  primary: 'border border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-on-primary)] hover:bg-[var(--color-primary-hover)]',
  secondary: 'border border-[var(--color-line)] bg-[var(--color-panel)] text-[var(--color-ink)] hover:bg-[var(--color-soft)]',
  ghost: 'border border-transparent bg-transparent text-[var(--color-primary)] hover:bg-[var(--color-primary-soft)]',
  ghostDanger: 'border border-transparent bg-transparent text-[var(--color-danger)] hover:bg-[var(--color-danger-soft)]',
  icon: 'border border-transparent bg-transparent text-[var(--color-muted)] hover:bg-[var(--color-soft)] hover:text-[var(--color-ink)]',
  danger: 'border border-[var(--color-danger)] bg-[var(--color-danger)] text-[var(--color-on-primary)] hover:bg-[var(--color-danger-ink)]'
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 text-xs',
  md: 'px-3.5 text-sm',
  lg: 'px-5 text-sm'
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  loadingLabel?: string
  loadingMode?: 'label' | 'spinner'
  /** 长文案按钮显式声明可换行；默认不换行且在 flex 里不被压缩 (LeapWorldTwin-233) */
  wrap?: boolean
  children: ReactNode
}

/**
 * 按钮视觉类现算（LeapWorldTwin-144 抽出）：`PageHeader` 的 `primaryAction.href` 分支要渲染
 * 视觉等同的 `<a>`（Button 本体只出 `<button>`），抽出这份类拼装避免两处字面量类名漂移。
 */
export function buttonClassNames({
  variant = 'primary',
  size = 'md',
  className = '',
  wrap = false
}: { variant?: ButtonVariant; size?: ButtonSize; className?: string; wrap?: boolean } = {}): string {
  // icon 变体补等宽与触控目标；数组拼装避免条件类留下的多余空格
  // Tailwind 4 preflight 下 <button> 默认 cursor:default，可点控件统一补 cursor-pointer；disabled 变体类压过它
  // 默认不换行且在 flex 里不被压缩（LeapWorldTwin-233：按钮文案换行把「两个字一行」带进页头/行内操作）；
  // 长文案按钮由调用方显式 wrap 声明才允许换行
  return [
    'inline-flex min-h-[var(--control-height)] cursor-pointer select-none items-center justify-center gap-1.5 rounded-[var(--radius-md)] py-1.5 font-medium transition-[color,background-color,border-color,transform] active:scale-[0.98] focus:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--color-focus)] disabled:cursor-not-allowed disabled:opacity-50 max-md:min-h-11',
    wrap ? '' : 'whitespace-nowrap shrink-0',
    variantClasses[variant],
    sizeClasses[size],
    variant === 'icon' ? 'min-w-[var(--control-height)] !px-0 max-md:min-w-11' : '',
    className
  ].filter(Boolean).join(' ')
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, loadingLabel = '处理中…', loadingMode = 'label', wrap = false, className = '', children, disabled, type = 'button', ...props },
  ref
) {
  const form = useContext(FormSubmitContext)
  // why: 默认 type=button，避免表单内取消钮隐式提交 (LeapWorldTwin-139)
  const formSubmitPending = type === 'submit' && !!form?.pending
  const formSubmitLocked = type === 'submit' && (!!form?.pending || !!form?.lockedAfterSuccess)
  const busy = loading || formSubmitPending
  const mode = formSubmitPending && !loading ? 'spinner' : loadingMode
  const classes = buttonClassNames({ variant, size, className, wrap })
  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled || busy || formSubmitLocked}
      aria-busy={busy || undefined}
      {...props}
    >
      {/* 内嵌转圈（spinner 模式）：prefers-reduced-motion 下静止，仅作状态指示 */}
      {busy && mode === 'spinner' ? <span className="h-3.5 w-3.5 rounded-[var(--radius-pill)] border-2 border-current border-r-transparent motion-safe:animate-spin" aria-hidden="true" /> : null}
      {busy && mode === 'label' ? loadingLabel : children}
    </button>
  )
})
